/* eslint-disable react-hooks/exhaustive-deps */
/**
 * AdminPageV2.jsx — auto-ported V2 of AdminPage.jsx.
 *
 * Backend: uniapp-backend (`staging-api.uniapp.vn`) via datV2.
 * Fields: camelCase (sessionId / startTime / vehiclePlate / etc).
 * Response: { data, pagination } envelope (auto-unwrapped by uniappClient).
 *
 * See docs/v2-migration-from-n8n.md for the full migration guide.
 */
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import datV2 from '../../services/datV2';
import {
  Badge,
  Button,
  Dropdown,
  Form,
  Modal,
  Pagination,
  Spinner,
  Table,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { VIOLATION_LIST, VIOLATION_FILTER_TERMS } from '../../constants/violation.constant';
import { PROVIDERS } from '../../constants/provider.constant';
import { ROLE } from '../../constants/roles.constant';
import { convertHoursToHM } from '../../utils';
import { tinhChiTieuDat } from '../../utils/tinhChiTieuDat';
import { catHaiChuSo } from '../../utils/dinhDangSo';
import V2PageLayout from '../../components/v2/V2PageLayout';
import { debounce } from 'lodash';
import SessionReportExportModal from '../../components/SessionReportExportModal';
import DatSummaryModal from '../../components/v2/DatSummaryModal';
import CopyableText from '../../components/CopyableText';

// Xoá phiên DAT cần quyền cấp approver (DAT_APPROVER 17 / admin 1, 10) —
// khớp BE guard requireDatApprover trên DELETE /v2/dat/session.
// Kiểm tra viên thường (DAT_ADMIN 15) không thấy cột Xóa (BE cũng 403).
function canDeleteSession() {
  try {
    const user = JSON.parse(localStorage.getItem('user-info') || 'null');
    const roles = Array.isArray(user?.role) ? user.role : [];
    return (
      roles.includes(ROLE.DRIVING.DAT_APPROVER) ||
      roles.includes(ROLE.DRIVING.ADMIN) ||
      roles.includes(ROLE.ADMIN)
    );
  } catch (_) {
    return false;
  }
}

// Đồng bộ Excel (Sheet) không có route ở BE V2 → chỉ hiển thị nút TP +
// Ecotek. (V1 render mọi PROVIDERS gồm cả Excel qua n8n.)
const SYNC_PROVIDERS = Object.values(PROVIDERS).filter((p) => p !== PROVIDERS.SHEET);

const CheckedHeaderComponent = ({ onOpenModal, allChecked }) => (
  <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
    <span className='me-2'>Đã kiểm tra</span>
    <Button
      size='sm'
      variant={allChecked ? 'outline-warning' : 'outline-primary'}
      onClick={onOpenModal}
      style={{ whiteSpace: 'nowrap' }}
    >
      {allChecked ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
    </Button>
  </div>
);

// ─── DRVN (DTLX) đối chiếu ──────────────────────────────────────────────
// Định dạng ngày YYYY-MM-DD (giờ VN) từ giá trị Date/ISO của phiên nội bộ.
const toDateStr = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const shiftDay = (dateStr, days) => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toDateStr(d);
};
const fmtCell = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
};
const toDateTimeStr = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString('vi-VN');
};
// Bảng ánh xạ trường: nhãn, giá trị nội bộ (local), giá trị DRVN (remote).
const buildCompareRows = (local, remote) => {
  const r = remote || {};
  return [
    { label: 'Mã phiên', local: local.sessionId, remote: r.maPhienHoc, compare: true },
    { label: 'Học viên', local: local.traineeName, remote: r.hoTenHocVien, compare: true },
    { label: 'Mã học viên', local: local.traineeRegistrationCode, remote: r.maHocVien },
    { label: 'Biển số', local: local.vehiclePlate, remote: r.bienSo, compare: true },
    { label: 'Giáo viên', local: local.instructorName, remote: r.hoTenGiaoVien },
    {
      label: 'Khoá học',
      local: local.courseShortName,
      remote: r.tenKhoaHoc && r.tenKhoaHoc !== 'N/A' ? r.tenKhoaHoc : (r.idKhoaHocText || r.maKhoaHoc),
    },
    { label: 'Bắt đầu', local: toDateTimeStr(local.startTime), remote: r.thoiGianBatDau },
    { label: 'Kết thúc', local: toDateTimeStr(local.endTime), remote: r.thoiGianKetThuc },
    { label: 'Quãng đường (km)', local: local.distance, remote: r.tongQuangDuong },
    { label: 'Thời gian (giờ)', local: local.duration, remote: r.tongThoiGian },
    { label: 'Trạng thái DRVN', local: null, remote: r.trangThaiText },
  ];
};

function AdminPageV2() {
  const params = new URLSearchParams(window.location.search);
  const [name, setName] = useState(params.get('name') || '');
  const [checked, setChecked] = useState(true);
  const [drivingClass, setDrivingClass] = useState(params.get('class') || '');
  const gridRef = useRef(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // Ép BE chạy thật cả khi khoảng ngày vừa được cron đồng bộ (xem
  // handleSyncBtn: BE trả skippedFresh thay vì gọi lại nhà cung cấp).
  const [forceSync, setForceSync] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [provider, setProvider] = useState('');
  // Tổng kết DAT (BE trả từ /v2/dat/student/summary) khi đang xem đúng 1
  // học viên — hiển thị trong modal (nút "Xem tổng kết DAT" trên toolbar).
  const [summaryDoc, setSummaryDoc] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [violation, setViolation] = useState('');
  const [rowData, setRowData] = useState([]);
  // Dải chỉ tiêu tính client-side như V1 (từ rowData camelCase).
  const [result, setResult] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [showCheckAllModal, setShowCheckAllModal] = useState(false);
  const [checkAllAction, setCheckAllAction] = useState('check'); // 'check' hoặc 'uncheck'
  const [showReportModal, setShowReportModal] = useState(false);
  // Soft delete confirmation. Hold the row being deleted so the modal
  // can show its identity (sessionId + trainee name). Set to null when
  // the modal is closed. `deleting` blocks the confirm button while the
  // BE call is in flight.
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // DRVN (DTLX) đối chiếu: giữ phiên nội bộ + kết quả tra cứu từ cổng DRVN.
  // null khi đóng modal. { local, loading, remote, error, date }.
  const [dtlxModal, setDtlxModal] = useState(null);

  const openDtlxCompare = useCallback(async (row) => {
    if (!row?.sessionId) {
      toast('Phiên này không có mã phiên để tra cứu DRVN.', { type: 'warning' });
      return;
    }
    const baseDate = toDateStr(row.startTime || row.loginAt || row.endTime);
    setDtlxModal({ local: row, loading: true, remote: null, error: '', date: baseDate });
    try {
      // Nới ±1 ngày để tránh lệch múi giờ (kết quả vẫn lọc đúng theo mã phiên).
      const dateFrom = baseDate ? shiftDay(baseDate, -1) : undefined;
      const dateTo = baseDate ? shiftDay(baseDate, 1) : undefined;
      const { data } = await datV2.getDtlxSession({ maPhienHoc: row.sessionId, dateFrom, dateTo });
      setDtlxModal((prev) => (prev ? { ...prev, loading: false, remote: data || null } : prev));
    } catch (err) {
      setDtlxModal((prev) =>
        prev ? { ...prev, loading: false, error: err?.response?.data?.message || err.message } : prev,
      );
    }
  }, []);

  const allChecked = useMemo(() => {
    if (rowData.length === 0) return false;
    return rowData.every(row => row.checked === true);
  }, [rowData]);

  const colDefs = useMemo(() => [
    {
      headerName: 'STT',
      valueGetter: (params) => (params.node ? params.node.rowIndex + 1 : ''),
      width: 60,
      pinned: 'left', // Cố định cột STT bên trái
    },
    {
      headerName: 'Tra cứu',
      width: 210,
      pinned: 'left',
      cellRenderer: (params) => {
        return (
          <div>
            <Button
              onClick={() => {
                const url = `/admin/session?name=${params.data.traineeRegistrationCode}`;
                window.open(url, '_blank');
              }}
            >
              Phiên học
            </Button>
            <Button
              className='ms-2'
              onClick={() => {
                const url = `/admin/session/student?name=${params.data.traineeRegistrationCode}`;
                window.open(url, '_blank');
              }}
            >
              Học viên
            </Button>
          </div>
        );
      },
    },
    // Cột Xóa chỉ hiện với người đủ quyền (spread trả [] khi không đủ)
    ...(canDeleteSession() ? [{
      headerName: 'Xóa',
      width: 90,
      pinned: 'left',
      cellRenderer: (params) => (
        <Button
          variant='outline-danger'
          size='sm'
          onClick={() => setSessionToDelete(params.data)}
          title='Xóa phiên (soft delete — có thể khôi phục từ DB)'
        >
          Xóa
        </Button>
      ),
    }] : []),
    {
      headerName: 'DRVN',
      width: 110,
      pinned: 'left',
      cellRenderer: (params) => (
        <Button
          variant='outline-info'
          size='sm'
          disabled={!params.data?.sessionId}
          onClick={() => openDtlxCompare(params.data)}
          title='Lấy dữ liệu phiên từ cổng DRVN (DTLX) để đối chiếu'
        >
          Đối chiếu
        </Button>
      ),
    },
    {
      field: 'source',
      headerName: 'Nguồn',
      flex: 1,
      minWidth: 90,
      valueGetter: (params) => {
        if (!params.data.source) return 'Excel';
        return params.data.source;
      },
    },
    {
      field: 'excelSync',
      headerName: 'Đồng bộ Excel',
      flex: 1,
      minWidth: 130,
      editable: true,
      valueGetter: (params) => {
        return params.data?.excelSync === true
          ? true
          : params.data?.excelSync === false
          ? false
          : null;
      },
      cellRenderer: 'agCheckboxCellRenderer',
    },
    {
      field: 'checked',
      headerName: 'Đã kiểm tra',
      flex: 1,
      minWidth: 250,
      editable: true,
      headerComponent: (props) => (
        <CheckedHeaderComponent
          allChecked={allChecked}
          onOpenModal={() => {
            if (rowData.length > 20) {
              toast('Chỉ có thể chọn/bỏ chọn tất cả tối đa 20 phiên. Vui lòng lọc dữ liệu trước.', { type: 'warning' });
              return;
            }
            setCheckAllAction(allChecked ? 'uncheck' : 'check');
            setShowCheckAllModal(true);
          }}
        />
      ),
      valueGetter: (params) => {
        return params.data?.checked === true
          ? true
          : params.data?.checked === false
          ? false
          : null;
      },
      cellRenderer: 'agCheckboxCellRenderer',
    },
    {
      field: 'violation',
      headerName: 'Vi phạm',
      editable: true,
      flex: 1,
      minWidth: 500,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: VIOLATION_LIST,
      },
    },
    {
      field: 'sessionId',
      headerName: 'Mã phiên',
      flex: 1,
      minWidth: 360,
      cellRenderer: (params) => {
        if (!params.value) return '';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {params.value}
            </span>
            <Button
              size='sm'
              variant='outline-secondary'
              title='Copy mã phiên'
              onClick={() => {
                navigator.clipboard
                  .writeText(params.value)
                  .then(() => toast(`Đã copy mã phiên ${params.value}`, { type: 'success' }))
                  .catch(() => toast('Copy thất bại', { type: 'error' }));
              }}
              style={{ whiteSpace: 'nowrap', padding: '2px 6px', lineHeight: 1 }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <rect x='9' y='9' width='13' height='13' rx='2' ry='2'></rect>
                <path d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1'></path>
              </svg>
            </Button>
          </div>
        );
      },
    },
    {
      field: 'startTime',
      headerName: 'Thời gian bắt đầu',
      cellRenderer: (params) => {
        return new Date(params.value).toLocaleString('en-GB', {
          timeZone: 'Asia/Ho_Chi_Minh',
        });
      },
    },
    {
      field: 'endTime',
      headerName: 'Thời gian kết thúc',
      cellRenderer: (params) => {
        return new Date(params.value).toLocaleString('en-GB', {
          timeZone: 'Asia/Ho_Chi_Minh',
        });
      },
    },
    { field: 'traineeName', headerName: 'Tên học viên', cellRenderer: (p) => <CopyableText value={p.value} /> },
    { field: 'traineeRegistrationCode', headerName: 'Mã học viên', cellRenderer: (p) => <CopyableText value={p.value} /> },
    {
      field: 'distance',
      headerName: 'Quãng đường',
      valueFormatter: (params) =>
        catHaiChuSo(params.value),
    },
    {
      field: 'duration',
      headerName: 'Thời gian',
      valueFormatter: (params) =>
        catHaiChuSo(params.value),
    },
    {
      field: 'autoDistance',
      headerName: 'Quãng đường TĐ',
      valueFormatter: (params) =>
        catHaiChuSo(params.value),
    },
    {
      field: 'autoDuration',
      headerName: 'Thời gian TĐ',
      valueFormatter: (params) =>
        catHaiChuSo(params.value),
    },
    {
      field: 'nightDistance',
      headerName: 'Quãng đường đêm',
      valueFormatter: (params) =>
        catHaiChuSo(params.value),
    },
    {
      field: 'nightDuration',
      headerName: 'Thời gian đêm',
      valueFormatter: (params) =>
        catHaiChuSo(params.value),
    },
    {
      field: 'nightDuration',
      headerName: 'Thời gian đêm đúng hạng',
      cellRenderer: (params) => {
        let val = 0;

        if (
          params.data.courseShortName?.includes('B1') ||
          params.data.courseShortName?.includes('BTĐ')
        ) {
          val = ['B1', 'B.01', 'B11'].includes(params.data.vehicleHang)
            ? params.data.nightDuration
              ? params.data.nightDuration
              : 0
            : 0;
        } else if (
          params.data.courseShortName?.includes('B2') ||
          params.data.courseShortName?.includes('BSS')
        ) {
          val = ['B2', 'B'].includes(params.data.vehicleHang)
            ? params.data.nightDuration
              ? params.data.nightDuration
              : 0
            : 0;
        } else if (params.data.courseShortName?.includes('C1')) {
          console.log(params.data.vehicleHang);
          val = ['C1', 'C'].includes(params.data.vehicleHang)
            ? params.data.nightDuration
              ? params.data.nightDuration
              : 0
            : 0;
        }
        return val;
      },
      valueFormatter: (params) =>
        catHaiChuSo(params.value),
    },
    {
      field: 'manualNightDuration',
      headerName: 'TG đêm thủ công',
      editable: true,
      valueFormatter: (params) =>
        catHaiChuSo(params.value),
    },
    {
      field: 'distanceSameHang',
      headerName: 'Quãng đường đúng hạng',
      valueFormatter: (params) =>
        catHaiChuSo(params.value),
    },
    {
      field: 'durationSameHang',
      headerName: 'Thời gian đúng hạng',
      valueFormatter: (params) =>
        catHaiChuSo(params.value),
    },
    { field: 'successRatio', headerName: 'Tỷ lệ nhận diện' },
    { field: 'courseShortName', headerName: 'Tên khoá học' },
    { field: 'vehicleHang', headerName: 'Hạng xe' },
    { field: 'vehiclePlate', headerName: 'Biển số xe' },
    { field: 'instructorName', headerName: 'Tên giáo viên' },
  ], [allChecked, rowData]);

  const getDatData = async (q) => {
    setLoading(true);
    try {
      const { data } = await datV2.query(q);
      setRowData(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const searchDatData = async (q) => {
    setLoading(true);
    try {
      const { data } = await datV2.search(q);
      setRowData(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckAllChange = useCallback(
    (isChecked) => {
      const updatedRows = rowData.map((row) => ({
        ...row,
        checked: isChecked,
      }));
      setRowData(updatedRows);

      updatedRows.forEach((row) => {
        const body = {
          sessionId: row.sessionId,
          checked: isChecked,
          violation: row.violation,
          excelSync: row.excelSync,
          manualNightDuration: row.manualNightDuration,
        };
        datV2
          .update(body)
          .then(() => {
            toast(
              `${isChecked ? 'Chọn' : 'Bỏ chọn'} thành công phiên ${
                row.sessionId
              } cho ${row.traineeName}`,
              { type: 'success' }
            );
          })
          .catch(() => {
            toast('Cập nhật thất bại', { type: 'error' });
          });
      });
    },
    [rowData]
  );

  const handleApplyCheckAll = () => {
    if (rowData.length > 20) {
      toast('Chỉ có thể chọn/bỏ chọn tất cả tối đa 20 phiên.', { type: 'warning' });
      setShowCheckAllModal(false);
      return;
    }
    const isChecked = checkAllAction === 'check';
    handleCheckAllChange(isChecked);
    setShowCheckAllModal(false);
    setSelectAllChecked(false);
  };

  const SYNC_CHUNK_DAYS = 4;

  const addDaysToDateStr = (dateStr, days) => {
    const d = new Date(`${dateStr}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  };

  const handleSyncBtn = async () => {
    if (!startDate || !endDate) {
      return toast('Vui lòng chọn thời gian', { type: 'warning' });
    }

    if (!provider) {
      return toast('Vui lòng chọn nhà cung cấp', { type: 'warning' });
    }

    const _startDate = new Date(`${startDate}T00:00:00Z`).getTime();
    const _endDate = new Date(`${endDate}T00:00:00Z`).getTime();

    if (_startDate > _endDate) {
      return toast('Ngày bắt đầu không thể lớn hơn ngày kết thúc', {
        type: 'warning',
      });
    }

    if (_endDate - _startDate > 16 * 24 * 60 * 60 * 1000) {
      return toast('Khoảng thời gian tối đa là 16 ngày', { type: 'warning' });
    }

    const totalDays =
      Math.round((_endDate - _startDate) / (24 * 60 * 60 * 1000)) + 1;
    const chunks = [];
    for (let i = 0; i < totalDays; i += SYNC_CHUNK_DAYS) {
      const chunkStart = addDaysToDateStr(startDate, i);
      const chunkEnd = addDaysToDateStr(
        startDate,
        Math.min(i + SYNC_CHUNK_DAYS - 1, totalDays - 1)
      );
      chunks.push({ start: chunkStart, end: chunkEnd });
    }

    setSyncing(true);

    let totalCount = 0;
    let totalFlagged = 0;
    const unmappedTpErrors = new Set();
    const failedChunks = [];
    const skippedChunks = [];
    try {
      for (let i = 0; i < chunks.length; i++) {
        const { start, end } = chunks[i];
        try {
          const res = await datV2.sync(
            provider,
            start,
            end,
            forceSync ? { force: true } : {}
          );
          // uniapp-backend returns { data: { fetched, upserted, inserted,
          // updated, flagged, unmappedTpViolations } }.
          // `upserted` = sessions that actually landed in our DB — that's
          // what the toast should report. (V1/n8n returned a raw
          // `{ count }`; the auto-port from V1 missed this rename.)
          // `flagged` = phiên có lỗi sau merge; `unmappedTpViolations` =
          // lỗi TP chưa có trong VIOLATION_LIST (đề xuất bổ sung danh mục).
          //
          // skippedFresh = BE KHÔNG gọi nhà cung cấp vì khoảng ngày này vừa
          // được cron đồng bộ xong (chống 429). Khi đó fetched/upserted là
          // số của LẦN CRON đó, không phải kết quả lượt bấm này — cộng vào
          // sẽ báo "đã đồng bộ N phiên" trong khi thực tế không chạy gì.
          if (res?.data?.skippedFresh) {
            const w = res.data.coveredWindow;
            skippedChunks.push(
              `${start} → ${end}${w ? ` (đã phủ bởi ${w.startDate} → ${w.endDate})` : ''}`
            );
            continue;
          }
          totalCount += res?.data?.upserted ?? res?.data?.fetched ?? 0;
          totalFlagged += res?.data?.flagged ?? 0;
          (res?.data?.unmappedTpViolations || []).forEach((v) =>
            unmappedTpErrors.add(v)
          );
        } catch (err) {
          failedChunks.push(`${start} → ${end}`);
        }
      }

      if (skippedChunks.length > 0) {
        toast(
          `${skippedChunks.length}/${chunks.length} đợt được bỏ qua vì vừa đồng bộ tự động xong: ${skippedChunks.join('; ')}. Tích "Bắt buộc đồng bộ" nếu vẫn muốn kéo lại.`,
          { type: 'warning', autoClose: false }
        );
      }

      if (unmappedTpErrors.size > 0) {
        toast(
          `Lỗi từ ${provider} chưa có trong danh mục vi phạm (đã lưu nguyên văn): ${[...unmappedTpErrors].join(' | ')}. Cân nhắc bổ sung vào danh mục.`,
          { type: 'info', autoClose: false }
        );
      }

      if (skippedChunks.length === chunks.length) {
        // Không đợt nào chạy thật — toast cảnh báo ở trên đã nói rõ lý do.
        // Giữ modal mở để người dùng tích "Bắt buộc đồng bộ" rồi bấm lại.
      } else if (failedChunks.length === 0) {
        const ranChunks = chunks.length - skippedChunks.length;
        toast(
          `Đã đồng bộ ${totalCount} phiên thành công (${ranChunks}/${chunks.length} đợt)` +
            (totalFlagged > 0 ? `, ${totalFlagged} phiên có lỗi` : ''),
          { type: 'success' }
        );
        setShowModal(false);
      } else if (totalCount > 0) {
        toast(
          `Đã đồng bộ ${totalCount} phiên, ${failedChunks.length}/${chunks.length} đợt lỗi: ${failedChunks.join('; ')}`,
          { type: 'warning' }
        );
      } else {
        toast('Đồng bộ lỗi toàn bộ các đợt, vui lòng thử lại', {
          type: 'error',
        });
      }
    } finally {
      setSyncing(false);
      getDatData({ page });
    }
  };

  // Flat query params matching the V2 BE (GET /v2/dat/search):
  //   name, drivingClass, violation, checked, page, limit.
  // (The old nested { search, filter, skip } shape was silently ignored by
  // the BE, so search + all filters did nothing.)
  const buildSearchParams = () => ({
    ...(name && { name }),
    ...(drivingClass && { drivingClass }),
    ...(violation && {
      violation: VIOLATION_FILTER_TERMS[violation] || violation,
    }),
    // checkbox unticked = chỉ hiện phiên chưa kiểm tra
    ...(!checked && { checked: false }),
    page,
    limit: 50,
  });

  const handleSearch = (e) => {
    e.preventDefault();

    if (!name) {
      alert('Vui lòng nhập tên/mã phiên/mã học viên cần tra cứu');
      return;
    }

    debouncedSearch(buildSearchParams());
  };

  const debouncedSearch = useMemo(() => debounce(searchDatData, 1000), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    debouncedSearch(buildSearchParams());
  }, [page, checked, violation, drivingClass, name]);

  const onBtnExportCsv = () => {
    gridRef.current.api.exportDataAsCsv();
  };

  const onCellValueChanged = (params) => {
    const { data } = params;
    const body = {
      sessionId: data.sessionId,
      checked: data.checked,
      violation: data.violation,
      excelSync: data.excelSync,
      manualNightDuration: data.manualNightDuration,
    };

    datV2
      .update(body)
      .then(() => {
        toast(`Cập nhật thành công phiên ${data.sessionId} cho ${data.traineeName}`, { type: 'success' });
      })
      .catch(() => {
        toast('Cập nhật thất bại', { type: 'error' });
      });
  };

  // Modal xác nhận trước khi chạy kiểm lỗi — liệt kê rõ những audit sẽ
  // chạy để admin biết mình đang kích hoạt gì (yêu cầu 2026-07-21).
  const [showCheckErrorModal, setShowCheckErrorModal] = useState(false);

  const onBtnCheckError = () => {
    setShowCheckErrorModal(false);
    setLoading(true);
    datV2.checkError().then((res) => {
      const d = res?.data || {};
      const parts = [
        d.underFiveMin > 0 && `${d.underFiveMin} phiên dưới 5 phút`,
        d.lowFaceId > 0 && `${d.lowFaceId} phiên nhận diện dưới 75%`,
        d.shortBreaks > 0 && `${d.shortBreaks} phiên nghỉ dưới 15 phút`,
      ].filter(Boolean);
      toast(
        parts.length
          ? `Kiểm lỗi xong — gắn lỗi mới: ${parts.join(', ')}`
          : 'Kiểm lỗi xong — không phát hiện lỗi mới',
        { type: 'success' }
      );
      getDatData({ page });
    }).catch((err) => {
      toast('Kiểm lỗi thất bại', { type: 'error' });
    }).finally(() => {
      setLoading(false);
    });
  }

  // Confirm + soft-delete a session. Uses mongoose-delete on the BE,
  // so the row is hidden from all default queries but the data stays
  // in the collection (recoverable via DB if needed).
  const handleConfirmDelete = async () => {
    if (!sessionToDelete?.sessionId) return;
    setDeleting(true);
    try {
      const res = await datV2.deleteSession(sessionToDelete.sessionId);
      // BE returns { data: { deleted: true|false, sessionId?, reason? } }.
      // `not_found` means the row was already deleted upstream — treat
      // as success for the UI since the end state is the same.
      const deleted = res?.data?.deleted === true;
      const reason = res?.data?.reason;
      if (deleted || reason === 'not_found') {
        toast(`Đã xóa phiên ${sessionToDelete.sessionId}`, { type: 'success' });
        setSessionToDelete(null);
        // Refresh the current page so the deleted row disappears.
        getDatData({ page });
      } else {
        toast('Xóa phiên thất bại — vui lòng thử lại', { type: 'error' });
      }
    } catch (err) {
      toast(`Xóa phiên lỗi: ${err?.response?.data?.message || err.message}`, { type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    setSelectAllChecked(false);
  }, [rowData]);

  // Dải chỉ tiêu (metrics strip) — tính client-side như V1 nhưng dùng
  // field camelCase của V2. Bỏ qua phiên có violation.
  //
  // Phép tính đã tách sang `utils/tinhChiTieuDat` để test được: tám tổng này
  // quyết định phán quyết đạt/chưa đạt của học viên, nên chúng cần một lưới
  // an toàn chạy được bằng jest chứ không chỉ nhìn bằng mắt trên giao diện.
  useEffect(() => {
    setResult(tinhChiTieuDat(rowData));
  }, [rowData]);

  // Tổng kết DAT nay do BE tính theo TỪNG học viên (GET /v2/dat/student/summary),
  // FE chỉ hiển thị. Chỉ lấy khi rowData thuộc đúng 1 học viên (mọi phiên
  // cùng traineeRegistrationCode) — tránh cộng nhầm nhiều học viên như trước.
  useEffect(() => {
    const first = rowData[0]?.traineeRegistrationCode;
    const single =
      rowData.length && rowData.every((r) => r.traineeRegistrationCode === first)
        ? first
        : '';
    if (!single) {
      setSummaryDoc(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await datV2.getStudentSummary(single);
        if (!cancelled) setSummaryDoc(data || null);
      } catch (err) {
        if (!cancelled) setSummaryDoc(null);
      }
    })();
    return () => { cancelled = true; };
  }, [rowData]);

  const StatusText = ({ status }) =>
    status === 'Đã đủ' ? (
      <span className='text-success'>{status}</span>
    ) : (
      <span className='text-danger'>{status}</span>
    );

  return (
    <>
    <V2PageLayout
      title='Phiên học DAT'
      filters={
        <>
          <Form.Control
            type='text'
            size='sm'
            style={{ width: 300 }}
            placeholder='Tên / mã phiên / mã học viên'
            aria-label='Tên, mã phiên hoặc mã học viên'
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Form.Control
            type='text'
            size='sm'
            style={{ width: 170 }}
            placeholder='Mã khoá học'
            aria-label='Mã khoá học'
            value={drivingClass}
            onChange={(e) => setDrivingClass(e.target.value)}
          />
          <Form.Select
            size='sm'
            style={{ width: 180 }}
            aria-label='Lọc theo lỗi vi phạm'
            value={violation}
            onChange={(e) => { setPage(1); setViolation(e.target.value); }}
            disabled={syncing}
          >
            <option value=''>Mọi lỗi vi phạm</option>
            {VIOLATION_LIST.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </Form.Select>
          <Form.Check
            inline
            className='mb-0 small'
            id='phien-chua-kiem'
            label='Phiên chưa kiểm tra'
            checked={!checked}
            type='checkbox'
            onChange={(e) => setChecked(!e.target.checked)}
          />
          <Button size='sm' onClick={handleSearch} variant='primary' disabled={loading}>
            Tra cứu
          </Button>
        </>
      }
      actions={
        <Dropdown align='end'>
          <Dropdown.Toggle size='sm' variant='outline-secondary' id='thao-tac-phien'>
            Thao tác
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {/* Ba mục đầu GHI dữ liệu hoặc chạy việc nặng; ba mục sau chỉ đọc.
                Tách khỏi thanh lọc để không ai bấm nhầm khi đang định lọc. */}
            {SYNC_PROVIDERS.map((p) => (
              <Dropdown.Item
                key={p}
                onClick={() => { setProvider(p); setShowModal(true); }}
              >
                Đồng bộ {p}
              </Dropdown.Item>
            ))}
            <Dropdown.Item onClick={() => setShowCheckErrorModal(true)}>
              Kiểm lỗi
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={onBtnExportCsv}>Xuất Excel</Dropdown.Item>
            <Dropdown.Item
              onClick={() => {
                if (!rowData.length) {
                  toast('Không có dữ liệu để xuất báo cáo', { type: 'warning' });
                  return;
                }
                setShowReportModal(true);
              }}
            >
              Xuất báo cáo
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item
              disabled={!summaryDoc}
              title={summaryDoc ? 'Xem tổng kết DAT của học viên' : 'Lọc theo 1 mã học viên để xem'}
              onClick={() => setShowSummaryModal(true)}
            >
              Tổng kết DAT
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      }
      summary={
        <div className='d-flex justify-content-between align-items-start flex-wrap gap-3 small dat-km'>
        <p>
          Tổng TG:{' '}
          <span className='fw-bold'>
            {convertHoursToHM(result?.totalDuration)}
            <br />(<StatusText status={result?.details?.totalTG} />)
          </span>
        </p>
        <p>
          Tổng QĐ:{' '}
          <span className='fw-bold'>
            {catHaiChuSo(result?.totalDistance)} km
            <br />(<StatusText status={result?.details?.totalQD} />)
          </span>
        </p>
        <p>
          TG đúng hạng:{' '}
          <span className='fw-bold'>
            {convertHoursToHM(result?.totalDurationDungHang)} <br />(
            <StatusText status={result?.details?.TG_dung_hang} />)
          </span>
        </p>
        <p>
          QĐ đúng hạng: {catHaiChuSo(result?.totalDistanceDungHang)} km
        </p>
        <p>
          TG đêm đúng hạng:{' '}
          <span className='fw-bold'>
            {convertHoursToHM(result?.totalNightDurationDungHang)}
            <br />
            <StatusText status={result?.details?.TG_ban_dem_dung_hang} />
          </span>
          {result?.manualNightDuration ? (
            <div>(TG nhập tay: {convertHoursToHM(result?.manualNightDuration)})</div>
          ) : null}
        </p>
        <p>
          TG tự động:{' '}
          <span className='fw-bold'>
            {convertHoursToHM(result?.totalAutoDuration)}
            <br />(<StatusText status={result?.details?.TG_tu_dong} />)
          </span>
        </p>
        <p>
          QĐ tự động:{' '}
          <span className={result?.totalAutoDistance > 120 ? 'text-danger fw-bold' : ''}>
            {catHaiChuSo(result?.totalAutoDistance)} km
          </span>
        </p>
        </div>
      }
      footer={
        <Pagination className='mb-0'>
          <Pagination.First onClick={() => setPage(1)} />
          <Pagination.Prev onClick={() => setPage(page - 1)} disabled={page === 1} />
          {page > 1 && (
            <Pagination.Item onClick={() => setPage(page - 1)}>{page - 1}</Pagination.Item>
          )}
          <Pagination.Item active>{page}</Pagination.Item>
          <Pagination.Item onClick={() => setPage(page + 1)}>{page + 1}</Pagination.Item>
          <Pagination.Next onClick={() => setPage(page + 1)} disabled={rowData.length < 20} />
        </Pagination>
      }
    >
        {loading && <p className='text-center'>Đang tải dữ liệu...</p>}
        {rowData.length && !loading && (
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={colDefs}
            onCellValueChanged={onCellValueChanged}
            gridOptions={{
              getRowStyle: (params) => {
                if (params.data?.violation?.length) {
                  return { backgroundColor: '#FF6666' };
                }
                return null;
              },
            }}
          />
        )}
        {!loading && !rowData.length && (
          <p className='text-center'>Không có dữ liệu</p>
        )}
    </V2PageLayout>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Đồng bộ {provider}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label>Từ ngày</Form.Label>
          <Form.Control
            type='date'
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Form.Label className='mt-3'>Đến ngày (bao gồm)</Form.Label>
          <Form.Control
            type='date'
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <Form.Check
            className='mt-3'
            type='checkbox'
            id='sync-force'
            label='Bắt buộc đồng bộ (bỏ qua chống trùng)'
            checked={forceSync}
            onChange={(e) => setForceSync(e.target.checked)}
          />
          <Form.Text muted>
            BE tự bỏ qua lượt bấm khi khoảng ngày này vừa được đồng bộ tự động
            xong, để không chạm giới hạn tần suất của {provider}. Chỉ tích ô này
            khi cần kéo lại bằng được.
          </Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowModal(false)}>
            Đóng
          </Button>
          <Button variant='primary' onClick={handleSyncBtn} disabled={syncing}>
            {syncing ? 'Đang đồng bộ...' : 'Bắt đầu đồng bộ'}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showCheckErrorModal} onHide={() => setShowCheckErrorModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Kiểm lỗi phiên học</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Hệ thống sẽ quét các phiên học và gắn cờ vi phạm cho:</p>
          <ul>
            <li><strong>Phiên học dưới 5 phút</strong> — thời lượng phiên nhỏ hơn 5 phút.</li>
            <li><strong>Tỷ lệ nhận diện dưới 75%</strong> — tỷ lệ nhận diện khuôn mặt của phiên thấp hơn 75%.</li>
            <li><strong>Nghỉ dưới 15 phút</strong> — khoảng nghỉ giữa 2 phiên liên tiếp của cùng học viên ngắn hơn 15 phút.</li>
          </ul>
          <p className='text-muted mb-0' style={{ fontSize: '0.875rem' }}>
            Chỉ các phiên chưa có vi phạm mới được gắn cờ — lỗi admin đã đánh
            dấu không bị thay đổi. Riêng <strong>trùng lặp giáo viên</strong>{' '}
            được hệ thống tự kiểm tra mỗi 30 phút, không cần chạy tay.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowCheckErrorModal(false)}>
            Đóng
          </Button>
          <Button variant='primary' onClick={onBtnCheckError} disabled={loading}>
            Xác nhận kiểm tra
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showCheckAllModal} onHide={() => setShowCheckAllModal(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            {checkAllAction === 'check' ? 'Chọn tất cả' : 'Bỏ chọn tất cả'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='mb-3'>
            {checkAllAction === 'check'
              ? `Sẽ chọn tất cả ${rowData.length} phiên sau:`
              : `Sẽ bỏ chọn tất cả ${rowData.length} phiên sau:`}
          </p>
          <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px', borderRadius: '4px' }}>
            <table style={{ width: '100%', fontSize: '14px' }}>
              <thead>
                <tr>
                  <th style={{ borderBottom: '1px solid #ddd', paddingBottom: '8px', paddingRight: '8px', textAlign: 'left' }}>STT</th>
                  <th style={{ borderBottom: '1px solid #ddd', paddingBottom: '8px', paddingRight: '8px', textAlign: 'left' }}>Mã học viên</th>
                  <th style={{ borderBottom: '1px solid #ddd', paddingBottom: '8px', paddingRight: '8px', textAlign: 'left' }}>Tên học viên</th>
                  <th style={{ borderBottom: '1px solid #ddd', paddingBottom: '8px', paddingRight: '8px', textAlign: 'left' }}>Mã phiên</th>
                </tr>
              </thead>
              <tbody>
                {rowData.map((row, index) => (
                  <tr key={row.sessionId} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ paddingTop: '8px', paddingBottom: '8px', paddingRight: '8px' }}>{index + 1}</td>
                    <td style={{ paddingTop: '8px', paddingBottom: '8px', paddingRight: '8px' }}>{row.traineeRegistrationCode}</td>
                    <td style={{ paddingTop: '8px', paddingBottom: '8px', paddingRight: '8px' }}>{row.traineeName}</td>
                    <td style={{ paddingTop: '8px', paddingBottom: '8px', paddingRight: '8px' }}>{row.sessionId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowCheckAllModal(false)}>
            Huỷ
          </Button>
          <Button
            variant={checkAllAction === 'check' ? 'success' : 'warning'}
            onClick={handleApplyCheckAll}
          >
            {checkAllAction === 'check' ? 'Chọn tất cả' : 'Bỏ chọn tất cả'}
          </Button>
        </Modal.Footer>
      </Modal>
      <SessionReportExportModal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        sessions={rowData}
      />
      <DatSummaryModal
        show={showSummaryModal}
        onHide={() => setShowSummaryModal(false)}
        summary={summaryDoc}
        studentName={rowData[0]?.traineeName}
      />
      <Modal
        show={!!sessionToDelete}
        onHide={() => !deleting && setSessionToDelete(null)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Xác nhận xóa phiên</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Bạn có chắc muốn xóa phiên này không? Phiên sẽ bị ẩn khỏi tất cả
            danh sách nhưng dữ liệu vẫn còn trong DB (có thể khôi phục bằng
            tay nếu cần).
          </p>
          {sessionToDelete && (
            <ul className='mb-0'>
              <li><strong>Mã phiên:</strong> {sessionToDelete.sessionId}</li>
              <li><strong>Học viên:</strong> {sessionToDelete.traineeName} ({sessionToDelete.traineeRegistrationCode})</li>
              <li><strong>Khóa:</strong> {sessionToDelete.courseShortName || '-'}</li>
              <li>
                <strong>Bắt đầu:</strong>{' '}
                {sessionToDelete.startTime
                  ? new Date(sessionToDelete.startTime).toLocaleString('en-GB', { timeZone: 'Asia/Ho_Chi_Minh' })
                  : '-'}
              </li>
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={() => setSessionToDelete(null)}
            disabled={deleting}
          >
            Hủy
          </Button>
          <Button
            variant='danger'
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? 'Đang xóa...' : 'Xác nhận xóa'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Đối chiếu phiên với cổng DRVN (DTLX) */}
      <Modal show={!!dtlxModal} onHide={() => setDtlxModal(null)} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title className='fs-6'>
            Đối chiếu phiên với DRVN
            {dtlxModal?.local?.sessionId && (
              <span className='text-muted ms-2' style={{ fontSize: 12 }}>
                {dtlxModal.local.sessionId}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {dtlxModal?.loading && (
            <div className='text-center py-4'>
              <Spinner animation='border' size='sm' /> Đang lấy dữ liệu từ DRVN…
            </div>
          )}

          {!dtlxModal?.loading && dtlxModal?.error && (
            <div className='alert alert-danger py-2'>{dtlxModal.error}</div>
          )}

          {!dtlxModal?.loading && !dtlxModal?.error && !dtlxModal?.remote && (
            <div className='alert alert-warning py-2 mb-0'>
              Không tìm thấy phiên <code>{dtlxModal?.local?.sessionId}</code> trên cổng DRVN
              {dtlxModal?.date ? ` quanh ngày ${dtlxModal.date}` : ''}. Có thể phiên chưa được
              truyền lên, hoặc lệch ngày.
            </div>
          )}

          {!dtlxModal?.loading && !dtlxModal?.error && dtlxModal?.remote && (
            <Table bordered size='sm' className='align-middle mb-0'>
              <thead>
                <tr>
                  <th style={{ width: '22%' }}>Trường</th>
                  <th>Hệ thống (nội bộ)</th>
                  <th>DRVN (DTLX)</th>
                </tr>
              </thead>
              <tbody>
                {buildCompareRows(dtlxModal.local, dtlxModal.remote).map((r) => {
                  const mismatch =
                    r.compare &&
                    r.local != null &&
                    r.remote != null &&
                    String(r.local).trim().toLowerCase() !== String(r.remote).trim().toLowerCase();
                  return (
                    <tr key={r.label} className={mismatch ? 'table-warning' : undefined}>
                      <td className='fw-semibold'>{r.label}</td>
                      <td>{fmtCell(r.local)}</td>
                      <td>
                        {fmtCell(r.remote)}
                        {mismatch && <Badge bg='warning' text='dark' className='ms-2'>khác</Badge>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setDtlxModal(null)}>Đóng</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default AdminPageV2;
